import React, { useEffect, useState } from 'react';
import { useNavigate, Route, Routes, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  CssBaseline,
  Drawer,
  Typography,
  Skeleton,
  IconButton,
  GlobalStyles,
  Tooltip,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Toaster } from 'react-hot-toast';

// Icons
import {
  LayoutDashboard,
  Package,
  BarChart3,
  FolderOpen,
  ClipboardList,
  Settings as SettingsIcon,
  User,
  HelpCircle,
  FileText,
  Menu,
  Thermometer,
  X,
  ChevronsLeft,
  ChevronsRight,
  Sun,
  Moon,
  LogOut,
  Clock,
} from 'lucide-react';

// Components
import Dashboard from './components/dashboard/Dashboard';
import GraphsPage from './components/graphs-page/GraphsPage';
import ProductDetailPage from './components/product-detail-page/ProductDetailPage';
import TodaysSalesProductDetailPage from './components/todays-sales-page/TodaysSalesProductDetailPage';
import ProductList from './components/product-list-page/ProductList';
import TodaysGraphsPage from './components/todays-sales-page/TodaysGraphsPage';
import POList from './components/po-list-page/POList';
import POCreateStep1 from './components/po-create-page/POCreateStep1';
import PODetails from './components/po-details-page/PODetails';
import POEdit from './components/po-edit-page/POEdit';
import CreateMasterPO from './components/master-po/CreateMasterPO';
import MasterPODetails from './components/master-po/MasterPODetails';
import MasterPOListPage from './pages/MasterPOListPage';
import InventoryCount from './components/inventory-count/InventoryCount';
import InventoryCountSessions from './components/inventory-count/InventoryCountSessions';
import ProductsWithoutBarcode from './components/inventory-count/ProductsWithoutBarcode';
import ReceiveMasterPO from './components/master-po/ReceiveMasterPO';
import Signup from './pages/Signup';
import Login from './pages/Login';
import Profile from './pages/Profile';
import Success from './pages/Success';
import Settings from './pages/Settings';
import Esp32DataPage from "./pages/Esp32DataPage";

import './App.css';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 60;

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoggedin, setIsLoggedin] = useState(false);
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [darkMode, setDarkMode] = useState(false);

  const [currentTime, setCurrentTime] = useState(new Date());

  const currentSidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const { pathname } = useLocation();
  const hideLayout = pathname === '/esp32-data';

  // Clock tick every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatCentralTime = () => {
    return currentTime.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const handleSignOut = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setIsLoggedin(false);
    setUser(null);
    navigate('/login');
  };

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
    document.body.classList.toggle('dark', darkMode);
  }, [darkMode]);

  // Notion-inspired theme
  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: '#2383e2' },
      secondary: { main: '#eb5757' },
      background: {
        default: darkMode ? '#191919' : '#ffffff',
        paper: darkMode ? '#202020' : '#ffffff',
      },
      text: {
        primary: darkMode ? 'rgba(255,255,255,0.81)' : '#37352f',
        secondary: darkMode ? 'rgba(255,255,255,0.44)' : '#787774',
      },
      divider: darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(55,53,47,0.09)',
      action: {
        hover: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(55,53,47,0.04)',
        selected: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(55,53,47,0.06)',
      },
    },
    typography: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      h1: { fontWeight: 700, letterSpacing: '-0.03em' },
      h2: { fontWeight: 700, letterSpacing: '-0.03em' },
      h3: { fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.5rem' },
      h5: { fontWeight: 600, letterSpacing: '-0.01em', fontSize: '1.15rem' },
      h6: { fontWeight: 600, letterSpacing: '-0.01em', fontSize: '1rem' },
      body1: { fontSize: '0.875rem', lineHeight: 1.65 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.6 },
      button: { fontWeight: 500, textTransform: 'none' },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: darkMode ? '#191919' : '#ffffff' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 6,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8125rem',
            padding: '6px 12px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(55,53,47,0.09)'}`,
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(55,53,47,0.09)'}`,
            borderRadius: 8,
            backgroundImage: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(55,53,47,0.09)'}`,
            padding: '10px 16px',
            fontSize: '0.8125rem',
          },
          head: {
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: darkMode ? 'rgba(255,255,255,0.44)' : '#787774',
            backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(55,53,47,0.024)',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(55,53,47,0.024)',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8125rem',
            minHeight: 40,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 2 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, fontSize: '0.75rem' },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 6,
              fontSize: '0.875rem',
              '& fieldset': {
                borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(55,53,47,0.16)',
              },
              '&:hover fieldset': {
                borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(55,53,47,0.3)',
              },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(55,53,47,0.09)'}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: darkMode ? '#202020' : '#fbfbfa',
            borderRight: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(55,53,47,0.09)'}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { display: 'none' },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8, fontSize: '0.8125rem' },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8125rem',
            borderRadius: 6,
          },
        },
      },
    },
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auth check
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    axios
      .get('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/auth/profile', {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        withCredentials: true,
      })
      .then((res) => {
        setUser(res.data);
        setIsLoggedin(true);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Authentication failed:', error.response?.data);
        setLoading(false);
        navigate('/login');
      });
  }, [navigate]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const navItems = [
    { text: 'Dashboard', icon: LayoutDashboard, path: '/' },
    { text: 'Products', icon: Package, path: '/product-list' },
    { text: 'Graphs', icon: BarChart3, path: '/graphs' },
    { text: 'Purchase Orders', icon: FolderOpen, path: '/master-pos' },
    { text: 'Inventory Count', icon: ClipboardList, path: '/inventory-count' },
  ];

  const bottomItems = [
    { text: 'PO Lists', icon: FileText, path: '/po-list' },
    { text: 'Settings', icon: SettingsIcon, path: '/settings' },
  ];

  const isActive = (path) => {
    if (path === '/') return pathname === '/';
    return pathname.startsWith(path);
  };

  const collapsed = !isMobile && sidebarCollapsed;

  const NavItem = ({ item, onClick }) => {
    const active = isActive(item.path);
    const Icon = item.icon;
    const content = (
      <Link
        to={item.path}
        onClick={onClick}
        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: collapsed ? 0 : 1.25,
            px: collapsed ? 0.75 : 1.5,
            py: 0.75,
            mx: 0.5,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.8125rem',
            fontWeight: active ? 600 : 400,
            color: active
              ? (darkMode ? 'rgba(255,255,255,0.9)' : '#37352f')
              : (darkMode ? 'rgba(255,255,255,0.5)' : '#787774'),
            backgroundColor: active
              ? (darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(55,53,47,0.06)')
              : 'transparent',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(55,53,47,0.04)',
              color: darkMode ? 'rgba(255,255,255,0.9)' : '#37352f',
            },
          }}
        >
          <Icon size={collapsed ? 18 : 16} strokeWidth={active ? 2.2 : 1.8} />
          {!collapsed && <span>{item.text}</span>}
        </Box>
      </Link>
    );

    if (collapsed) {
      return (
        <Tooltip title={item.text} placement="right" arrow>
          {content}
        </Tooltip>
      );
    }
    return content;
  };

  const sidebar = (
    <Box
      className="notion-sidebar"
      sx={{
        backgroundColor: darkMode ? '#202020' : '#fbfbfa',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Logo / Brand + Collapse toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          px: collapsed ? 0.5 : 1.5,
          py: 1,
          mb: 1,
        }}
      >
        {collapsed ? (
          <IconButton
            onClick={() => setSidebarCollapsed(false)}
            size="small"
            sx={{
              color: darkMode ? 'rgba(255,255,255,0.5)' : '#787774',
              '&:hover': { color: darkMode ? 'rgba(255,255,255,0.9)' : '#37352f' },
            }}
          >
            <ChevronsRight size={18} />
          </IconButton>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/shopifyicon.png" alt="Logo" style={{ width: 32, height: 32, borderRadius: 6 }} />
              <Typography
                sx={{
                  ml: 1,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  letterSpacing: '-0.01em',
                  color: darkMode ? 'rgba(255,255,255,0.81)' : '#37352f',
                }}
              >
                Inventory
              </Typography>
            </Box>
            {!isMobile && (
              <IconButton
                onClick={() => setSidebarCollapsed(true)}
                size="small"
                sx={{
                  color: darkMode ? 'rgba(255,255,255,0.35)' : '#b4b4b0',
                  '&:hover': { color: darkMode ? 'rgba(255,255,255,0.7)' : '#37352f' },
                }}
              >
                <ChevronsLeft size={16} />
              </IconButton>
            )}
          </>
        )}
      </Box>

      {/* Main nav */}
      <Box sx={{ flex: 1 }}>
        <Box className="notion-nav-section">
          {navItems.map((item) => (
            <NavItem
              key={item.text}
              item={item}
              onClick={() => isMobile && setMobileOpen(false)}
            />
          ))}
        </Box>
      </Box>

      {/* Bottom section */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
        {/* Doc link */}
        {collapsed ? (
          <Tooltip title="Documentation" placement="right" arrow>
            <a
              href="https://shopifyappdocumentation.mustafaemresahin.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 0.75,
                  mx: 0.5,
                  borderRadius: '6px',
                  color: darkMode ? 'rgba(255,255,255,0.5)' : '#787774',
                  '&:hover': {
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(55,53,47,0.04)',
                    color: darkMode ? 'rgba(255,255,255,0.9)' : '#37352f',
                  },
                }}
              >
                <HelpCircle size={18} strokeWidth={1.8} />
              </Box>
            </a>
          </Tooltip>
        ) : (
          <a
            href="https://shopifyappdocumentation.mustafaemresahin.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 0.75,
                mx: 0.5,
                borderRadius: '6px',
                fontSize: '0.8125rem',
                color: darkMode ? 'rgba(255,255,255,0.5)' : '#787774',
                '&:hover': {
                  backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(55,53,47,0.04)',
                  color: darkMode ? 'rgba(255,255,255,0.9)' : '#37352f',
                },
              }}
            >
              <HelpCircle size={16} strokeWidth={1.8} />
              <span>Documentation</span>
            </Box>
          </a>
        )}

        {bottomItems.map((item) => (
          <NavItem
            key={item.text}
            item={item}
            onClick={() => isMobile && setMobileOpen(false)}
          />
        ))}

        {/* User */}
        {loading ? (
          <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Skeleton variant="circular" width={22} height={22} />
            {!collapsed && <Skeleton variant="text" width="60%" />}
          </Box>
        ) : collapsed ? (
          <Tooltip title={user?.name || 'Profile'} placement="right" arrow>
            <Link
              to="/profile"
              style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  py: 0.75,
                  mx: 0.5,
                  mt: 0.5,
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(55,53,47,0.04)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: '#2383e2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                  }}
                >
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </Box>
              </Box>
            </Link>
          </Tooltip>
        ) : (
          <Link
            to="/profile"
            onClick={() => isMobile && setMobileOpen(false)}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 0.75,
                mx: 0.5,
                mt: 0.5,
                borderRadius: '6px',
                fontSize: '0.8125rem',
                color: darkMode ? 'rgba(255,255,255,0.5)' : '#787774',
                '&:hover': {
                  backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(55,53,47,0.04)',
                },
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  backgroundColor: '#2383e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Box>
              <span style={{ fontWeight: 500 }}>{user?.name || 'Profile'}</span>
            </Box>
          </Link>
        )}
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: darkMode ? '#2a2a2a' : '#fff',
            color: darkMode ? 'rgba(255,255,255,0.9)' : '#37352f',
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(55,53,47,0.1)'}`,
            fontSize: '0.8125rem',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          },
          success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
        }}
      />
      <GlobalStyles
        styles={{
          html: { backgroundColor: darkMode ? '#191919' : '#ffffff' },
          body: { backgroundColor: darkMode ? '#191919' : '#ffffff' },
        }}
      />
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {/* Sidebar */}
        {!hideLayout && !loading && isLoggedin && (
          <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? mobileOpen : true}
            onClose={handleDrawerToggle}
            anchor="left"
            sx={{
              [`& .MuiDrawer-paper`]: {
                width: isMobile ? SIDEBAR_WIDTH : currentSidebarWidth,
                boxSizing: 'border-box',
                top: 0,
                border: 'none',
                borderRight: `1px solid ${darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(55,53,47,0.09)'}`,
                transition: 'width 0.2s ease',
                overflowX: 'hidden',
              },
            }}
          >
            {sidebar}
          </Drawer>
        )}

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: '100vh',
            marginLeft: hideLayout || isMobile || !isLoggedin ? 0 : `${currentSidebarWidth}px`,
            backgroundColor: darkMode ? '#191919' : '#ffffff',
            transition: 'margin-left 0.2s ease',
          }}
        >
          {/* Top Bar */}
          {!hideLayout && isLoggedin && !loading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 1.5, sm: 3, md: 4 },
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: darkMode ? '#202020' : '#fbfbfa',
                position: 'sticky',
                top: 0,
                zIndex: 1100,
                gap: 1,
                minHeight: 48,
              }}
            >
              {/* Left: hamburger on mobile */}
              {isMobile && (
                <IconButton
                  onClick={handleDrawerToggle}
                  size="small"
                  sx={{
                    color: darkMode ? 'rgba(255,255,255,0.6)' : '#787774',
                    mr: 0.5,
                  }}
                >
                  {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                </IconButton>
              )}

              {/* Center: Date & Time */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flex: 1,
                  justifyContent: isMobile ? 'center' : 'flex-start',
                }}
              >
                <Clock size={14} style={{ opacity: 0.5 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: '0.01em',
                  }}
                >
                  {formatCentralTime()} CT
                </Typography>
              </Box>

              {/* Right: Dark mode toggle, profile, sign out */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={darkMode ? 'Light mode' : 'Dark mode'}>
                  <IconButton
                    onClick={() => setDarkMode(!darkMode)}
                    size="small"
                    sx={{
                      color: darkMode ? 'rgba(255,255,255,0.6)' : '#787774',
                      '&:hover': { color: darkMode ? 'rgba(255,255,255,0.9)' : '#37352f' },
                    }}
                  >
                    {darkMode ? <Sun size={16} /> : <Moon size={16} />}
                  </IconButton>
                </Tooltip>

                <Tooltip title={user?.name || 'Profile'}>
                  <IconButton
                    onClick={() => navigate('/profile')}
                    size="small"
                    sx={{
                      color: darkMode ? 'rgba(255,255,255,0.6)' : '#787774',
                      '&:hover': { color: darkMode ? 'rgba(255,255,255,0.9)' : '#37352f' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: '#2383e2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                      }}
                    >
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </Box>
                  </IconButton>
                </Tooltip>

                <Tooltip title="Sign out">
                  <IconButton
                    onClick={handleSignOut}
                    size="small"
                    sx={{
                      color: darkMode ? 'rgba(255,255,255,0.45)' : '#b4b4b0',
                      '&:hover': { color: '#ef4444' },
                    }}
                  >
                    <LogOut size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          <Box sx={{ maxWidth: pathname === '/graphs' ? 'none' : 1200, mx: 'auto', px: { xs: 1.5, sm: 3, md: 4 }, py: { xs: 1.5, md: 3 } }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/success" element={<Success />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/login" element={<Login />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/product-list" element={<ProductList />} />
              <Route path="/graphs" element={<GraphsPage />} />
              <Route path="/todays-graphs" element={<TodaysGraphsPage />} />
              <Route path="/product/:id" element={<ProductDetailPage />} />
              <Route path="/todays-graphs/product/:id" element={<TodaysSalesProductDetailPage />} />
              <Route path="/po-list" element={<POList />} />
              <Route path="/po-create-step1" element={<POCreateStep1 />} />
              <Route path="/po/:id" element={<PODetails />} />
              <Route path="/po-edit/:id" element={<POEdit />} />
              <Route path="/create-master-po" element={<CreateMasterPO />} />
              <Route path="/master-po/:id" element={<MasterPODetails />} />
              <Route path="/master-pos" element={<MasterPOListPage />} />
              <Route path="/inventory-count" element={<InventoryCountSessions />} />
              <Route path="/inventory-count/:sessionId" element={<InventoryCount />} />
              <Route path="/inventory-count/barcodes" element={<ProductsWithoutBarcode />} />
              <Route path="/master-po/:id/receive" element={<ReceiveMasterPO />} />
              <Route path="/settings" element={<Settings darkMode={darkMode} setDarkMode={setDarkMode} />} />
              <Route path="/esp32-data" element={<Esp32DataPage />} />
            </Routes>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
